import { BaseSavedScenario, BaseScenarioData } from '../types/shared';
import API_CONFIG from '../config/api';
import authService from './auth-service';

export interface BaseScenario extends BaseSavedScenario {
  data: BaseScenarioData;
}

export abstract class BaseScenariosService<T extends BaseScenario> {
  protected abstract readonly STORAGE_KEY: string;
  protected abstract readonly SCENARIO_TYPE: 'car' | 'housing' | 'career' | 'retirement';
  private apiUrl = `${API_CONFIG.BASE_URL}/scenarios`;

  /**
   * Save a new scenario to backend
   */
  async saveScenario(scenario: Omit<T, 'id' | 'createdAt'>): Promise<string> {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify({
          name: scenario.name,
          description: scenario.description || '',
          scenario_type: this.SCENARIO_TYPE,
          scenario_data: scenario.data
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save scenario');
      }

      const data = await response.json();
      return data.id.toString();
    } catch (error) {
      console.error('Error saving scenario:', error);
      throw error;
    }
  }

  /**
   * Get all scenarios from backend
   */
  async getAllScenarios(): Promise<Record<string, T>> {
    try {
      const response = await fetch(`${this.apiUrl}?type=${this.SCENARIO_TYPE}`, {
        method: 'GET',
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load scenarios');
      }

      const scenarios: any[] = await response.json();

      // Convert array to Record<string, T>
      // Now the list endpoint includes scenario_data, so no need for individual fetches!
      const scenariosRecord: Record<string, T> = {};
      for (const s of scenarios) {
        // Properly nest scenario_data under 'data' field
        scenariosRecord[s.id.toString()] = {
          id: s.id.toString(),
          name: s.name,
          description: s.description,
          createdAt: new Date(s.created_at).getTime(),
          lastModified: s.updated_at,
          data: s.scenario_data  // Nest data properly instead of spreading
        } as unknown as T;
      }

      return scenariosRecord;
    } catch (error) {
      console.error('Error loading scenarios:', error);
      return {};
    }
  }

  /**
   * Get a specific scenario by ID from backend
   */
  async getScenario(id: string): Promise<T | null> {
    try {
      const response = await fetch(`${this.apiUrl}/${id}`, {
        method: 'GET',
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      // Convert backend format to frontend format
      return {
        id: data.id.toString(),
        name: data.name,
        description: data.description || '',
        data: data.scenario_data,
        createdAt: new Date(data.created_at).getTime()
      } as unknown as T;
    } catch (error) {
      console.error('Error loading scenario:', error);
      return null;
    }
  }

  /**
   * Update an existing scenario in backend
   */
  async updateScenario(id: string, updates: Partial<T>): Promise<boolean> {
    try {
      const payload: any = {};
      if (updates.name) payload.name = updates.name;
      if (updates.description !== undefined) payload.description = updates.description;
      if (updates.data) payload.scenario_data = updates.data;

      const response = await fetch(`${this.apiUrl}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify(payload)
      });

      return response.ok;
    } catch (error) {
      console.error('Error updating scenario:', error);
      return false;
    }
  }

  /**
   * Delete a scenario from backend
   */
  async deleteScenario(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl}/${id}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });

      return response.ok;
    } catch (error) {
      console.error('Error deleting scenario:', error);
      return false;
    }
  }

  /**
   * Get scenarios as an array (for easier iteration)
   */
  async getScenariosArray(): Promise<T[]> {
    const scenarios = await this.getAllScenarios();
    return Object.values(scenarios).sort((a, b) => b.createdAt - a.createdAt);
  }

  /**
   * Clear all scenarios (delete all from backend)
   */
  async clearAllScenarios(): Promise<void> {
    const scenarios = await this.getAllScenarios();
    const deletePromises = Object.keys(scenarios).map(id => this.deleteScenario(id));
    await Promise.all(deletePromises);
  }
}
